import * as React from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { mergeStyleSets, nuOrderTheme as theme, ConfirmDialog, TextField } from "@nuorder/components";
import { AssortmentType } from "containers/folders/types/base";
import { formatMessage } from "lib/globalize-util";
import {
    ActionsTypes,
    AssortmentDialogContextProvider,
    AssortmentTypeSelector,
    BrandPicker,
    CloseoutAssortmentDropdown,
    DimensionsDropdowns,
    FormValues,
    MerchantDropdownOptions,
    useAssortmentDialogReducer,
} from "components";
import { FOLDERS } from "constants/integration/folders";
import { isFormValid } from "./utils";
import { DATATEST } from "constants/integration";
import { KEY_DOT } from "containers/folders/utils/constants";
import { appendDateSuffix } from "helpers/dates";

const classNames = mergeStyleSets({
    messageError: {
        fontSize: 14,
        color: theme.palette.redDark,
        paddingTop: 10,
        wordBreak: "break-word",
    },
    textField: {
        " .ms-TextField-fieldGroup": {
            border: "none",
        },
    },
});

type CreateAssortmentFormProps = {
    dimensionsDropdownOptions: MerchantDropdownOptions[];
    formValues: FormValues;
    onSubmit: (formValues: FormValues) => void;
    isLoading: boolean;
    onClose: () => void;
    errorMessage: string;
    brandId: string;
    isVerticalAssortmentEnabled: boolean;
};

export const CreateAssortmentForm: React.FunctionComponent<CreateAssortmentFormProps> = ({
    onSubmit,
    isLoading,
    onClose,
    dimensionsDropdownOptions,
    errorMessage,
    formValues,
    brandId,
    isVerticalAssortmentEnabled,
}) => {
    const flags = useFlags();

    const [assortmentName, setAssortmentName] = React.useState(appendDateSuffix(formatMessage("Untitled Assortment")));

    const [state, dispatch] = useAssortmentDialogReducer({
        isEditing: false,
        formValues: {
            assortmentName: assortmentName,
        } as FormValues,
    });

    React.useEffect(() => {
        formValues.assortmentName = appendDateSuffix(formatMessage("Untitled Assortment"));

        if (isVerticalAssortmentEnabled) {
            formValues.assortmentType = AssortmentType.Vertical;
            formValues.brandId = brandId;

            dispatch({ type: ActionsTypes.UPDATE_ASSORTMENT_TYPE, payload: AssortmentType.Vertical });
        }

        dispatch({ type: ActionsTypes.UPDATE_FORM_VALUES, payload: formValues });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [assortmentType, setAssortmentType] = React.useState<boolean>(true);

    const handleClearValues = () =>
        dispatch({ type: ActionsTypes.UPDATE_ASSORTMENT_TYPE, payload: AssortmentType.External });

    const shouldRenderCloseoutDropdown =
        flags.closeoutAssortment && state.formValues.assortmentType === AssortmentType.External;

    const handleKeyDownName = React.useCallback((event) => {
        if (event.keyCode === KEY_DOT) {
            event.preventDefault();
        }
    }, []);

    const handleOnPaste = React.useCallback((e: React.ClipboardEvent) => {
        const clipboardData = e.clipboardData;
        const pastedText = clipboardData.getData("Text");
        if (pastedText.indexOf(".") !== -1) {
            e.preventDefault();
        }
    }, []);

    const onChangeAssortmentName = (event) => {
        setAssortmentName(event.target.value);
        dispatch({ type: ActionsTypes.UPDATE_ASSORTMENT_NAME, payload: event.target.value as string });
    };

    return (
        <ConfirmDialog
            contentProps={{ title: formatMessage("New Assortment") }}
            onConfirm={() => onSubmit(state.formValues)}
            onClose={onClose}
            open={true}
            isLoading={isLoading}
            onFinished={handleClearValues}
            confirmButtonDisabled={isLoading || !isFormValid(state.formValues)}
            data-test={FOLDERS.CREATE_ASSORTMENT_DIALOG.CONTAINER}
            okLabel={formatMessage("Create")}
            cancelLabel={formatMessage("Cancel")}
            closeButtonAriaLabel={formatMessage("Close")}
            isBlocking={true}
        >
            <AssortmentDialogContextProvider value={{ ...state, dispatch }}>
                <TextField
                    data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.NAME}
                    required
                    value={assortmentName}
                    onChange={onChangeAssortmentName}
                    label={formatMessage("Assortment Name")}
                    onKeyDown={handleKeyDownName}
                    onPaste={handleOnPaste}
                    className={classNames.textField}
                />
                {!isVerticalAssortmentEnabled && <AssortmentTypeSelector setAssortmentType={setAssortmentType} />}
                <DimensionsDropdowns merchantDimensionsOptions={dimensionsDropdownOptions} />
                {!isVerticalAssortmentEnabled && assortmentType && <BrandPicker />}
                {!isVerticalAssortmentEnabled && shouldRenderCloseoutDropdown && <CloseoutAssortmentDropdown />}
            </AssortmentDialogContextProvider>
            <p data-test={FOLDERS.CREATE_ASSORTMENT_DIALOG.MESSAGE_ERROR} className={classNames.messageError}>
                {errorMessage}
            </p>
        </ConfirmDialog>
    );
};
